import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    Sparkles, Plus, Play, XCircle, RefreshCw, Trophy, Users, Ticket, Clock, UserPlus, X, Undo2,
} from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtCT = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return ''; }
};

const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Wheels Admin/Operator Panel — create ad-hoc "Wheel the Names" promotions.
 *
 * Admin sees all operators and must pick one; operator can only create for
 * their own storefront. Cashiers get read-only via this same panel (create
 * form hidden).
 */
export const WheelsAdminPanel = ({ role = 'admin' }) => {
    const [rows, setRows] = useState([]);
    const [operators, setOperators] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState({
        name: 'Wheel the Names',
        prize_amount: 25,
        min_load: 20,
        starts_at: toLocalInput(new Date(Date.now() + 5 * 60 * 1000)),
        ends_at: toLocalInput(new Date(Date.now() + 65 * 60 * 1000)),
        operator_id: '',
        included_operator_ids: [],   // admin-only: extra operators whose players see this wheel
    });
    const [saving, setSaving] = useState(false);

    const canCreate = role === 'admin' || role === 'operator';
    // Cashiers can add / erase players (scoped to their pool by the backend)
    // but not create new wheels. Cancel + Fire are shared with cashiers per
    // wheels router (both endpoints already require get_cashier_user).
    const canManageRow = role === 'admin' || role === 'operator' || role === 'cashier';

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/wheels?include_settled=true&limit=30`, { withCredentials: true });
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load wheels');
        } finally { setLoading(false); }
    }, []);

    const loadOperators = useCallback(async () => {
        if (role !== 'admin') return;
        try {
            const { data } = await axios.get(`${API}/admin/users?role=operator&limit=500`, { withCredentials: true });
            const list = Array.isArray(data) ? data : (data?.users || data?.data || []);
            // Only true operators
            setOperators(list.filter(u => u.role === 'operator'));
        } catch {
            // Fallback: hierarchy tree
            try {
                const { data } = await axios.get(`${API}/hierarchy/tree`, { withCredentials: true });
                const collected = [];
                const walk = (n) => {
                    if (!n) return;
                    if (n.role === 'operator') collected.push(n);
                    (n.children || []).forEach(walk);
                };
                (Array.isArray(data) ? data : [data]).forEach(walk);
                setOperators(collected);
            } catch { /* silent */ }
        }
    }, [role]);

    useEffect(() => { load(); loadOperators(); }, [load, loadOperators]);
    useEffect(() => {
        const t = setInterval(load, 30_000);
        return () => clearInterval(t);
    }, [load]);

    const active = useMemo(() => rows.filter(r => ['scheduled', 'active'].includes(r.status)), [rows]);
    const settled = useMemo(() => rows.filter(r => ['settled', 'cancelled'].includes(r.status)), [rows]);

    const submit = async () => {
        setSaving(true);
        try {
            const body = {
                name: form.name.trim() || 'Wheel of Names',
                prize_amount: Number(form.prize_amount),
                min_load: Number(form.min_load),
                starts_at: new Date(form.starts_at).toISOString(),
                ends_at: new Date(form.ends_at).toISOString(),
            };
            if (role === 'admin') {
                body.operator_id = form.operator_id;
                // Only send included list if admin picked at least one
                if (form.included_operator_ids?.length) {
                    body.included_operator_ids = form.included_operator_ids.filter(x => x && x !== form.operator_id);
                }
            }
            const { data } = await axios.post(`${API}/wheels`, body, { withCredentials: true });
            toast.success(`Wheel created — ${data.name}`);
            setShowCreate(false);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to create wheel');
        } finally { setSaving(false); }
    };

    const cancel = async (id) => {
        if (!window.confirm('Cancel this wheel? Entries and prize are voided.')) return;
        try {
            await axios.post(`${API}/wheels/${id}/cancel`, {}, { withCredentials: true });
            toast.success('Wheel cancelled');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Cancel failed');
        }
    };

    const fireNow = async (id) => {
        if (!window.confirm('Fire this wheel now (before its end time)?')) return;
        try {
            const { data } = await axios.post(`${API}/wheels/${id}/fire`, {}, { withCredentials: true });
            toast.success(`Winner: ${data.winner_name || 'no entrants'}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Fire failed');
        }
    };

    // Erase a player from a wheel — pulls all their entries AND adds them to
    // the wheel's exclusion list so the auto-sync won't re-add them.
    const erasePlayer = async (wheelId, userId, name) => {
        if (!window.confirm(`Erase ${name || 'this player'} from the wheel?\nAll of their tickets will be removed and they won't be re-added even if they load again.`)) return;
        try {
            await axios.delete(`${API}/wheels/${wheelId}/player/${userId}`, { withCredentials: true });
            toast.success(`${name || 'Player'} erased from wheel`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erase failed');
        }
    };

    // Undo — bring an erased player back
    const restorePlayer = async (wheelId, userId, name) => {
        try {
            await axios.post(`${API}/wheels/${wheelId}/player/${userId}/restore`, {}, { withCredentials: true });
            toast.success(`${name || 'Player'} restored — will re-appear on next sync`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Restore failed');
        }
    };

    // ---- Manual entry dialog state ----
    const [manualDialog, setManualDialog] = useState({ open: false, wheel: null });
    const [playerQuery, setPlayerQuery] = useState('');
    const [playerResults, setPlayerResults] = useState([]);
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [manualTickets, setManualTickets] = useState(1);
    const [manualNote, setManualNote] = useState('');
    const [manualSaving, setManualSaving] = useState(false);

    const openManualAdd = (wheel) => {
        setManualDialog({ open: true, wheel });
        setPlayerQuery('');
        setPlayerResults([]);
        setSelectedPlayer(null);
        setManualTickets(1);
        setManualNote('');
    };

    // Debounced player search — uses the admin OR cashier endpoint depending
    // on the caller's role. Cashiers get scoped to their own pool via
    // /cashier/players (already has `q=` support), admins hit /admin/users
    // (added `q` support server-side 2026-02-26).
    useEffect(() => {
        if (!manualDialog.open) return;
        if (!playerQuery || playerQuery.trim().length < 2) {
            setPlayerResults([]);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const url = role === 'cashier'
                    ? `${API}/cashier/players?q=${encodeURIComponent(playerQuery.trim())}`
                    : `${API}/admin/users?role=user&limit=15&q=${encodeURIComponent(playerQuery.trim())}`;
                const { data } = await axios.get(url, { withCredentials: true });
                const list = Array.isArray(data) ? data : (data?.users || data?.data || []);
                setPlayerResults(list);
            } catch { /* silent */ }
        }, 250);
        return () => clearTimeout(t);
    }, [playerQuery, manualDialog.open, role]);

    const submitManual = async () => {
        if (!selectedPlayer || !manualDialog.wheel) {
            toast.error('Pick a player first');
            return;
        }
        setManualSaving(true);
        try {
            await axios.post(
                `${API}/wheels/${manualDialog.wheel.id}/manual-entry`,
                {
                    user_id: selectedPlayer.id || selectedPlayer._id,
                    tickets: Number(manualTickets) || 1,
                    note: manualNote || undefined,
                },
                { withCredentials: true }
            );
            toast.success(`Added ${selectedPlayer.name} with ${manualTickets} ticket${manualTickets === 1 ? '' : 's'}`);
            setManualDialog({ open: false, wheel: null });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to add entry');
        }
        setManualSaving(false);
    };

    const opName = (opId) => {
        const o = operators.find(x => (x.id || x._id) === opId);
        return o?.name || opId?.slice(-6) || '—';
    };

    return (
        <div className="space-y-5" data-testid="wheels-admin-panel">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-[#FFD700]" />
                        Wheel the Names
                    </h2>
                    <p className="text-sm text-[#A0AAB2]">Ad-hoc promo · 1 entry per $20 loaded · house-funded</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={load} disabled={loading} data-testid="wheels-refresh-btn">
                        <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                    {canCreate && (
                        <Button
                            onClick={() => setShowCreate(true)}
                            className="bg-[#FFD700] text-black hover:bg-[#e6c200]"
                            data-testid="wheels-create-btn"
                        >
                            <Plus className="w-4 h-4 mr-1" />
                            New Wheel
                        </Button>
                    )}
                </div>
            </div>

            {/* Active / scheduled */}
            <section>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#A0AAB2] mb-2">Active &amp; scheduled</h3>
                {active.length === 0 ? (
                    <div className="border border-dashed border-white/10 rounded-2xl p-6 text-center text-sm text-[#8891A0]" data-testid="wheels-empty-active">
                        No active wheels. Create one to get started.
                    </div>
                ) : (
                    <div className="grid gap-3">
                        {active.map(w => (
                            <WheelRow
                                key={w.id}
                                w={w}
                                showOperator={role === 'admin'}
                                opName={opName(w.operator_id)}
                                canManage={canManageRow}
                                onCancel={() => cancel(w.id)}
                                onFire={() => fireNow(w.id)}
                                onAddManual={role === 'admin' || role === 'cashier' ? () => openManualAdd(w) : null}
                                onErasePlayer={role === 'admin' || role === 'cashier' ? (uid, name) => erasePlayer(w.id, uid, name) : null}
                                onRestorePlayer={role === 'admin' || role === 'cashier' ? (uid, name) => restorePlayer(w.id, uid, name) : null}
                            />
                        ))}
                    </div>
                )}
            </section>

            {/* Settled / cancelled */}
            <section>
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#A0AAB2] mb-2">History</h3>
                {settled.length === 0 ? (
                    <div className="border border-dashed border-white/10 rounded-2xl p-6 text-center text-sm text-[#8891A0]">
                        No past wheels yet.
                    </div>
                ) : (
                    <div className="grid gap-3">
                        {settled.slice(0, 15).map(w => (
                            <WheelRow
                                key={w.id}
                                w={w}
                                showOperator={role === 'admin'}
                                opName={opName(w.operator_id)}
                                canManage={false}
                            />
                        ))}
                    </div>
                )}
            </section>

            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="bg-[#0e0e0e] border border-white/10 text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white">New Wheel</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Players earn 1 entry per <b>${form.min_load || 20}</b> of <b>real money</b> loaded during the window —
                            OCR-auto PayPal receipts, admin deposits, and manual cashier counter loads.
                            <span className="block text-[11px] text-[#FF8C00] mt-1">
                                Comp, loan, free play and bonus grants do <b>not</b> count.
                            </span>
                            Prize is house-funded and paid as <b>bonus credits with 1× wagering</b> (same rules as Free Play).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-[#A0AAB2]">Name</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="wheel-form-name" />
                        </div>
                        {role === 'admin' && (
                            <div>
                                <Label className="text-[#A0AAB2]">Operator (storefront)</Label>
                                <select
                                    className="w-full h-10 px-3 rounded-md bg-black border border-white/15 text-sm"
                                    value={form.operator_id}
                                    onChange={e => setForm({ ...form, operator_id: e.target.value, included_operator_ids: form.included_operator_ids.filter(x => x !== e.target.value) })}
                                    data-testid="wheel-form-operator"
                                >
                                    <option value="">— Pick operator —</option>
                                    {operators.map(o => (
                                        <option key={o.id || o._id} value={o.id || o._id}>
                                            {o.name}{o.is_house ? ' (house pool)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Admin-only: opt in additional operators whose players will also see this
                            wheel on their dashboards. Tickets still only accrue from loads on the
                            primary operator above — this is purely a visibility broadcast. */}
                        {role === 'admin' && (
                            <div>
                                <Label className="text-[#A0AAB2]">
                                    Also visible to (optional)
                                    <span className="text-[10px] text-[#666] ml-1 font-normal">
                                        · view-only, tickets still route via primary operator
                                    </span>
                                </Label>
                                {operators.filter(o => (o.id || o._id) !== form.operator_id).length === 0 ? (
                                    <p className="text-[11px] text-[#666] italic py-2">Pick a primary operator above to see other options.</p>
                                ) : (
                                    <div className="mt-1 border border-white/10 rounded-md bg-black/40 max-h-32 overflow-y-auto divide-y divide-white/5" data-testid="wheel-form-included-ops">
                                        {operators.filter(o => (o.id || o._id) !== form.operator_id).map(o => {
                                            const oid = o.id || o._id;
                                            const checked = form.included_operator_ids.includes(oid);
                                            return (
                                                <label key={oid} className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 cursor-pointer text-sm">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={e => setForm({
                                                            ...form,
                                                            included_operator_ids: e.target.checked
                                                                ? [...form.included_operator_ids, oid]
                                                                : form.included_operator_ids.filter(x => x !== oid),
                                                        })}
                                                        className="accent-[#FFD700]"
                                                        data-testid={`wheel-form-include-${oid}`}
                                                    />
                                                    <span className="text-white">{o.name}</span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                )}
                                {form.included_operator_ids.length > 0 && (
                                    <p className="text-[10px] text-[#39FF14] mt-1">
                                        + {form.included_operator_ids.length} additional storefront{form.included_operator_ids.length === 1 ? '' : 's'} will see this wheel
                                    </p>
                                )}
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[#A0AAB2]">Prize $</Label>
                                <Input type="number" min="1" step="1" value={form.prize_amount} onChange={e => setForm({ ...form, prize_amount: e.target.value })} data-testid="wheel-form-prize" />
                            </div>
                            <div>
                                <Label className="text-[#A0AAB2]">Min load per entry $</Label>
                                <Input type="number" min="1" step="1" value={form.min_load} onChange={e => setForm({ ...form, min_load: e.target.value })} data-testid="wheel-form-minload" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[#A0AAB2]">Starts (local)</Label>
                                <Input type="datetime-local" value={form.starts_at} onChange={e => setForm({ ...form, starts_at: e.target.value })} data-testid="wheel-form-start" />
                            </div>
                            <div>
                                <Label className="text-[#A0AAB2]">Ends (local) · auto-fires</Label>
                                <Input type="datetime-local" value={form.ends_at} onChange={e => setForm({ ...form, ends_at: e.target.value })} data-testid="wheel-form-end" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button
                            onClick={submit}
                            disabled={saving || (role === 'admin' && !form.operator_id)}
                            className="bg-[#FFD700] text-black hover:bg-[#e6c200]"
                            data-testid="wheel-form-submit"
                        >
                            {saving ? 'Creating…' : 'Create Wheel'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Manual add-player dialog (admin-only) */}
            <Dialog open={manualDialog.open} onOpenChange={(o) => setManualDialog(o ? manualDialog : { open: false, wheel: null })}>
                <DialogContent className="bg-[#0e0e0e] border border-white/10 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-[#FFD700]" /> Add player manually
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Drops the player into <b className="text-white">{manualDialog.wheel?.name}</b> right now.
                            Doesn&apos;t cost anything and doesn&apos;t reference a load transaction.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-[#A0AAB2]">Search player</Label>
                            <Input
                                value={playerQuery}
                                onChange={e => { setPlayerQuery(e.target.value); setSelectedPlayer(null); }}
                                placeholder="Name or phone…"
                                autoFocus
                                data-testid="wheel-manual-search"
                            />
                            {playerResults.length > 0 && !selectedPlayer && (
                                <div className="mt-1 border border-white/10 rounded-md max-h-40 overflow-y-auto">
                                    {playerResults.map(p => (
                                        <button
                                            type="button"
                                            key={p.id || p._id}
                                            onClick={() => { setSelectedPlayer(p); setPlayerQuery(p.name || ''); setPlayerResults([]); }}
                                            className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm border-b border-white/5 last:border-b-0"
                                            data-testid={`wheel-manual-pick-${p.id || p._id}`}
                                        >
                                            <div className="text-white">{p.name}</div>
                                            <div className="text-[10px] text-[#A0AAB2] font-mono">{p.phone}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                            {selectedPlayer && (
                                <div className="mt-1 rounded-md border border-[#FFD700]/40 bg-[#FFD700]/10 px-3 py-2 text-sm">
                                    <span className="text-white font-semibold">{selectedPlayer.name}</span>
                                    <span className="text-[#A0AAB2] ml-2 text-xs font-mono">{selectedPlayer.phone}</span>
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[#A0AAB2]">Tickets</Label>
                                <Input type="number" min="1" max="500" value={manualTickets}
                                    onChange={e => setManualTickets(Math.max(1, Number(e.target.value) || 1))}
                                    data-testid="wheel-manual-tickets" />
                            </div>
                            <div>
                                <Label className="text-[#A0AAB2]">Note (optional)</Label>
                                <Input value={manualNote} onChange={e => setManualNote(e.target.value)}
                                    placeholder="e.g. cash tip"
                                    data-testid="wheel-manual-note" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setManualDialog({ open: false, wheel: null })}>Cancel</Button>
                        <Button onClick={submitManual} disabled={!selectedPlayer || manualSaving}
                            className="bg-[#FFD700] text-black hover:bg-[#e6c200]"
                            data-testid="wheel-manual-submit">
                            {manualSaving ? 'Adding…' : `Add ${manualTickets} ticket${manualTickets === 1 ? '' : 's'}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const WheelRow = ({ w, showOperator, opName, canManage, onCancel, onFire, onAddManual, onErasePlayer, onRestorePlayer }) => {
    const now = Date.now();
    const startsMs = new Date(w.starts_at).getTime();
    const endsMs = new Date(w.ends_at).getTime();
    const isScheduled = w.status === 'scheduled';
    const isActive = w.status === 'active';
    const isSettled = w.status === 'settled';
    const isCancelled = w.status === 'cancelled';

    const [remaining, setRemaining] = useState(() => Math.max(0, (isScheduled ? startsMs : endsMs) - now));
    const [showAllEntries, setShowAllEntries] = useState(false);
    const [entryQuery, setEntryQuery] = useState('');
    useEffect(() => {
        if (isSettled || isCancelled) return;
        const t = setInterval(() => setRemaining(Math.max(0, (isScheduled ? startsMs : endsMs) - Date.now())), 1000);
        return () => clearInterval(t);
    }, [isSettled, isCancelled, isScheduled, startsMs, endsMs]);
    const mins = Math.floor(remaining / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);

    const statusColor = isActive ? 'text-[#00FF88]' : isScheduled ? 'text-[#FFD700]' : isSettled ? 'text-[#A0AAB2]' : 'text-[#FF3B30]';

    return (
        <div className="bg-[#121212] border border-white/10 rounded-2xl p-4" data-testid={`wheel-row-${w.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-white">{w.name}</span>
                        <span className={`text-xs font-bold uppercase ${statusColor}`}>{w.status}</span>
                    </div>
                    <div className="text-xs text-[#A0AAB2] mt-1">
                        {showOperator && <span>Operator: <b className="text-white">{opName}</b> · </span>}
                        Prize <b className="text-[#FFD700]">${w.prize_amount}</b> · Min load <b className="text-white">${w.min_load}</b>
                    </div>
                    <div className="text-xs text-[#A0AAB2] mt-1 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {fmtCT(w.starts_at)} → {fmtCT(w.ends_at)} CT
                    </div>
                </div>
                <div className="text-right">
                    <div className="flex items-center gap-3 text-sm">
                        <span className="flex items-center gap-1 text-white"><Users className="w-4 h-4 text-[#A0AAB2]" />{w.unique_players}</span>
                        <span className="flex items-center gap-1 text-white"><Ticket className="w-4 h-4 text-[#A0AAB2]" />{w.total_tickets}</span>
                    </div>
                    {!isSettled && !isCancelled && (
                        <div className="text-xs text-[#A0AAB2] mt-1">
                            {isScheduled ? 'Starts in ' : 'Fires in '} <b className="text-white">{mins}m {secs}s</b>
                        </div>
                    )}
                    {isSettled && w.winner_name && (
                        <div className="text-xs mt-1 flex items-center gap-1 justify-end">
                            <Trophy className="w-3 h-3 text-[#FFD700]" />
                            <span className="text-white font-bold">{w.winner_name}</span>
                        </div>
                    )}
                </div>
            </div>

            {(w.entries || []).length > 0 && (
                <div className="mt-3">
                    {(w.entries || []).length > 6 && (
                        <div className="mb-2 relative">
                            <input
                                type="text"
                                value={entryQuery}
                                onChange={(e) => setEntryQuery(e.target.value)}
                                placeholder={`Search ${(w.entries || []).length} entries…`}
                                className="w-full pl-3 pr-8 py-1.5 text-xs rounded-lg bg-black/40 border border-white/10 text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FFD700]"
                                data-testid={`wheel-entry-search-${w.id}`}
                            />
                            {entryQuery && (
                                <button
                                    type="button"
                                    onClick={() => setEntryQuery('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-black/50 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center"
                                    title="Clear search"
                                    data-testid={`wheel-entry-search-clear-${w.id}`}
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    )}
                    {(() => {
                        const q = entryQuery.trim().toLowerCase();
                        const filtered = q
                            ? (w.entries || []).filter((e) => (e.name || '').toLowerCase().includes(q))
                            : (w.entries || []);
                        const visible = (q || showAllEntries) ? filtered : filtered.slice(0, 12);
                        return (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {visible.map((e, i) => (
                                        <div
                                            key={`${e.load_txn_id}-${i}`}
                                            className={`relative text-xs px-2 py-1.5 rounded-lg border ${w.winner_user_id === e.user_id ? 'border-[#FFD700] bg-[#FFD700]/10' : 'border-white/10 bg-black/40'}`}
                                            data-testid="wheel-entry-chip"
                                        >
                                            <div className="font-semibold text-white truncate pr-5">{e.name}</div>
                                            <div className="text-[#A0AAB2]">
                                                ${e.load_amount?.toFixed(0)} · {e.tickets} tix
                                            </div>
                                            {/* Erase button — only when admin has edit power on a live wheel */}
                                            {onErasePlayer && (isActive || isScheduled) && (
                                                <button
                                                    onClick={() => onErasePlayer(e.user_id, e.name)}
                                                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 hover:bg-[#FF3B30] text-white/70 hover:text-white flex items-center justify-center transition"
                                                    title={`Erase ${e.name} from this wheel`}
                                                    data-testid={`wheel-erase-${e.user_id}`}
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {!q && !showAllEntries && filtered.length > 12 && (
                                        <button
                                            type="button"
                                            onClick={() => setShowAllEntries(true)}
                                            className="text-xs px-2 py-1.5 rounded-lg border border-[#FFD700]/40 bg-black/40 text-[#FFD700] hover:bg-[#FFD700]/10 flex items-center justify-center font-semibold"
                                            data-testid={`wheel-show-all-${w.id}`}
                                        >
                                            +{filtered.length - 12} more — show all
                                        </button>
                                    )}
                                </div>
                                {!q && showAllEntries && filtered.length > 12 && (
                                    <div className="mt-2 flex justify-center">
                                        <button
                                            type="button"
                                            onClick={() => setShowAllEntries(false)}
                                            className="text-[10px] uppercase tracking-widest text-[#A0AAB2] hover:text-white"
                                            data-testid={`wheel-collapse-${w.id}`}
                                        >
                                            Collapse
                                        </button>
                                    </div>
                                )}
                                {q && filtered.length === 0 && (
                                    <div className="text-xs text-[#A0AAB2] italic text-center py-3" data-testid={`wheel-no-match-${w.id}`}>
                                        No entries match &ldquo;{entryQuery}&rdquo;
                                    </div>
                                )}
                                {q && filtered.length > 0 && (
                                    <div className="text-[10px] uppercase tracking-widest text-[#A0AAB2] mt-2 text-center">
                                        Showing {filtered.length} of {(w.entries || []).length}
                                    </div>
                                )}
                            </>
                        );
                    })()}
                    {/* Erased-players banner — lets the admin restore anyone they removed */}
                    {onRestorePlayer && (w.excluded_user_ids || []).length > 0 && (isActive || isScheduled) && (
                        <div className="mt-2 rounded-lg border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-2" data-testid={`wheel-excluded-${w.id}`}>
                            <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold mb-1 flex items-center gap-1">
                                <XCircle className="w-3 h-3" />
                                Erased from this wheel ({(w.excluded_user_ids || []).length})
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {(w.excluded_user_ids || []).map((uid) => (
                                    <button
                                        key={uid}
                                        onClick={() => onRestorePlayer(uid, 'Player')}
                                        className="text-[10px] px-2 py-0.5 rounded-full bg-black/40 border border-white/10 text-[#c9e5d4] hover:text-white hover:border-[#39FF14] flex items-center gap-1"
                                        title="Restore this player — they'll re-appear on next sync"
                                        data-testid={`wheel-restore-${uid}`}
                                    >
                                        <Undo2 className="w-2.5 h-2.5" />
                                        {uid.slice(-6)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {canManage && (isActive || isScheduled) && (
                <div className="mt-3 flex gap-2 justify-end flex-wrap">
                    {onAddManual && (
                        <Button size="sm" variant="outline" onClick={onAddManual} className="border-[#FFD700]/60 text-[#FFD700] hover:bg-[#FFD700]/10" data-testid={`wheel-add-manual-${w.id}`}>
                            <UserPlus className="w-4 h-4 mr-1" /> Add player
                        </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={onCancel} className="text-[#FF3B30] hover:bg-[#FF3B30]/10" data-testid={`wheel-cancel-${w.id}`}>
                        <XCircle className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                    <Button size="sm" onClick={onFire} className="bg-[#00FF88] text-black hover:bg-[#00cc66]" data-testid={`wheel-fire-${w.id}`}>
                        <Play className="w-4 h-4 mr-1" /> Fire now
                    </Button>
                </div>
            )}
        </div>
    );
};

export default WheelsAdminPanel;
